import _ from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';

import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';

import CandoBolsContainer from '../../containers/billing/CandoBols';
import AuthUserContainer from '../../containers/AuthUser';

import { getSharedStyles } from '../../themes';
import EnhancedTable from '../../components/table/EnhancedTable';
import { Typography } from '@material-ui/core';

import ResourcePopup from '../../components/Dialogs/ResourcePopup';
import CandoBolUploadDialog from '../../components/Dialogs/CandoBolUploadDialog';
import { grey, blue } from '@material-ui/core/colors';
import BolLinkedStatus, { LINKED_STATUS_ENUM } from '../../components/billing/BolLinkedStatus';

import invoiceIcon from '../../images/invoicePdf.svg';

const UPLOAD_BOL_ENABLED = true;

const INTAKE_TYPE_ENUM = {
	manual: 'manual',
	emailIngestion: 'emailIngestion',
};

const intakeTypeToDisplayMap = {
	[INTAKE_TYPE_ENUM.manual]: 'Manual',
	[INTAKE_TYPE_ENUM.emailIngestion]: 'Email Ingestion',
};

const intakeTypeToColorMap = {
	[INTAKE_TYPE_ENUM.manual]: grey[500],
	[INTAKE_TYPE_ENUM.emailIngestion]: blue[500],
};

function intakeTypeToDisplay(intakeType) {
	return intakeTypeToDisplayMap[intakeType] || 'Manual';
}

const styles = (theme) => ({
	...getSharedStyles(theme),
	uploadButton: {
		borderRadius: 100,
	},
	invoiceIcon: {
		display: 'inline-block',
		width: 40,
		height: 48,
		backgroundImage: `url('${invoiceIcon}')`,
		backgroundPosition: 'center',
		backgroundSize: 'cover',
		verticalAlign: 'middle',
		position: 'relative',
		flexShrink: 0,
		cursor: 'pointer',
	},
});

const columns = [
	{
		sortId: 'candoBolId',
		displayId: 'candoBolId',
		label: 'ID',
	},
	{
		sortId: 'bolNumber',
		displayId: 'bolNumber',
		label: 'BOL #',
	},
	{
		sortId: 'railcarNumber1',
		displayId: 'railcarDisplay',
		label: 'Railcar #',
	},
	{
		sortId: 'poNumberRaw',
		displayId: 'poNumberRaw',
		label: 'PO # / Loading #',
	},
	{
		sortId: 'destinationStationNumber',
		displayId: 'destinationDisplay',
		label: 'Destination',
	},
	{
		sortId: 'startDateTime',
		displayId: 'startFinishDisplay',
		label: 'Start/Finish',
	},
	{
		sortId: 'netGallons',
		displayId: 'gallonsDisplay',
		label: 'Gallons',
	},
	{
		noSort: true,
		sortId: 'bolPdfResourceId',
		displayId: 'bolPdfDisplay',
		label: 'PDF',
	},
	{
		sortId: 'intakeType',
		displayId: 'intakeDisplay',
		label: 'Intake',
	},
	{
		sortId: 'linkedStationDeliveryId',
		displayId: 'linkedStationDeliveryDisplay',
		label: 'Linked Delivery',
	},
	{
		sortId: 'linkedStatus',
		displayId: 'linkedStatusDisplay',
		label: 'Linked Status',
	},
];

class CandoBols extends Component {
	constructor(props) {
		super(props);

		this.state = {
			order: 'desc',
			orderBy: 'candoBolId',
			isResourcePopupOpen: false,
			resourceIdsPoppedUp: [],
			isUploadOpen: false,
			reparsingCandoBolId: null,
		};
	}

	componentDidMount() {
		const { candoBols } = this.props;

		if (!candoBols) {
			this.refreshTable();
		} else {
			this.formatCandoBols();
		}
	}

	componentDidUpdate(prevProps) {
		const { candoBols } = this.props;
		if (prevProps.candoBols !== candoBols) {
			this.formatCandoBols();
		}
	}

	refreshTable = () => {
		this.props.candoBolSearch({
			afterDateTime: moment().subtract(2, 'months').format('YYYY-MM-DD'),
		});
	};

	formatCandoBols = () => {
		const { classes, candoBols, stationMap, isDwp } = this.props;
		const { reparsingCandoBolId } = this.state;

		const rows = [];
		(candoBols || []).forEach((candoBol) => {
			const { candoBolId, startDateTime, finishDateTime, dateCreated } = candoBol;

			candoBol.key = candoBolId;
			candoBol.startDateTimeFormatted = startDateTime
				? moment(startDateTime).format('M/D/YY h:mm a')
				: '-';
			candoBol.finishDateTimeFormatted = finishDateTime
				? moment(finishDateTime).format('M/D/YY h:mm a')
				: '-';
			candoBol.dateCreatedFormatted = dateCreated
				? moment(dateCreated).format('M/D/YY h:mm a')
				: '-';

			candoBol.startFinishDisplay = (
				<div>
					<div>Start: {candoBol.startDateTimeFormatted}</div>
					<div>Finish: {candoBol.finishDateTimeFormatted}</div>
				</div>
			);

			candoBol.gallonsDisplay = (
				<div>
					<div>Net: {candoBol.netGallons}</div>
					<div>Gross: {candoBol.grossGallons}</div>
				</div>
			);

			// A Cando split BOL covers two railcars — show each railcar with its
			// per-railcar net gallons on its own line.
			candoBol.railcarDisplay = (
				<div>
					<div style={{ whiteSpace: 'nowrap' }}>
						{candoBol.railcarNumber1 || '-'}
						{candoBol.railcarNumber1 && !_.isNil(candoBol.netGallons1)
							? ` (${candoBol.netGallons1})`
							: ''}
					</div>
					{candoBol.railcarNumber2 && (
						<div style={{ whiteSpace: 'nowrap' }}>
							{candoBol.railcarNumber2}
							{!_.isNil(candoBol.netGallons2) ? ` (${candoBol.netGallons2})` : ''}
						</div>
					)}
				</div>
			);

			candoBol.destinationDisplay =
				candoBol.destinationStationNumber || candoBol.destinationRaw || '-';

			candoBol.bolPdfDisplay = candoBol.bolPdfResourceId ? (
				<div
					className={classes.invoiceIcon}
					onClick={() => {
						this.handlePdfClick(candoBol.bolPdfResourceId);
					}}
				/>
			) : (
				'-'
			);

			// DEV-only (dwp): re-run the parser + link on a row that failed under an
			// older parser. Rows that are already railcar-linked are refused server-side.
			const canReparse = isDwp && candoBol.linkedStatus !== LINKED_STATUS_ENUM.railcarLinked;
			candoBol.linkedStatusDisplay = (
				<div>
					<BolLinkedStatus
						linkedStatus={candoBol.linkedStatus}
						message={candoBol.linkedErrorMessage}
					/>
					{canReparse && (
						<Button
							size="small"
							variant="outlined"
							color="secondary"
							style={{ marginTop: 6, whiteSpace: 'nowrap' }}
							disabled={reparsingCandoBolId === candoBolId}
							onClick={() => {
								this.handleReparse(candoBolId);
							}}
						>
							{reparsingCandoBolId === candoBolId ? 'Reparsing...' : 'DEV Only: Reparse'}
						</Button>
					)}
				</div>
			);

			candoBol.intakeDisplay = (
				<div>
					<div style={{ color: intakeTypeToColorMap[candoBol.intakeType], fontWeight: 'bold' }}>
						Type: {intakeTypeToDisplay(candoBol.intakeType)}
					</div>
					<div>Date Created: {candoBol.dateCreatedFormatted}</div>
				</div>
			);

			if (candoBol.linkedStationDeliveryId) {
				const station = stationMap[candoBol.linkedStationId] || { number: 'Missing' };
				candoBol.linkedStationDeliveryDisplay = (
					<div>
						<div style={{ whiteSpace: 'nowrap' }}>
							Station Delivery #
							<a
								href={`/logistics/stationDelivery/update/${candoBol.linkedStationDeliveryId}`}
								style={{ textDecoration: 'none', paddingTop: 5, paddingLeft: 5 }}
								target="_blank"
								rel="noreferrer noopener"
							>
								<strong>{candoBol.linkedStationDeliveryId}</strong>
							</a>
						</div>
						<div>
							Station: {station.number} / Tank: {candoBol.linkedTankNumber}
						</div>
					</div>
				);
			}

			rows.push(candoBol);
		});
		this.setState({ rows });
	};

	handleReparse = (candoBolId) => {
		const { candoBolReparse } = this.props;
		this.setState({ reparsingCandoBolId: candoBolId }, this.formatCandoBols);
		candoBolReparse({
			params: { candoBolId },
			// apiFetch already surfaces errors via ERROR_EVENT; just refresh either
			// way so the row's new parse/link status shows.
			callback: () => {
				this.setState({ reparsingCandoBolId: null }, this.formatCandoBols);
				this.refreshTable();
			},
		});
	};

	handleOpenUpload = () => {
		this.setState({ isUploadOpen: true });
	};

	handleCloseUpload = () => {
		this.setState({ isUploadOpen: false });
	};

	handlePdfClick = (resourceId) => {
		this.setState({
			resourceIdsPoppedUp: [resourceId],
			isResourcePopupOpen: true,
		});
	};

	handleCloseResourcePopup = () => {
		this.setState({
			isResourcePopupOpen: false,
			resourceIdsPoppedUp: [],
		});
	};

	render() {
		const { classes, resourceMap, candoBolUpload, showErrors } = this.props;
		const { rows, order, orderBy } = this.state;

		if (!rows) {
			return <div>Loading the Cando BOLs...</div>;
		}

		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Grid container spacing={3} alignItems="center">
						<Grid item xs={8}>
							<Typography variant="h5" align="left" gutterBottom className={classes.title}>
								<span>Cando BOLs</span>
							</Typography>
						</Grid>
						<Grid item xs={4} style={{ textAlign: 'right' }}>
							<Button
								variant="outlined"
								color="primary"
								className={classes.uploadButton}
								style={{ marginRight: 10 }}
								onClick={this.refreshTable}
							>
								Refresh
							</Button>
							{UPLOAD_BOL_ENABLED && (
								<Button
									variant="contained"
									color="primary"
									className={classes.uploadButton}
									onClick={this.handleOpenUpload}
								>
									Upload BOL
								</Button>
							)}
						</Grid>
					</Grid>

					<Paper className={classes.paper}>
						<EnhancedTable
							columns={columns}
							rows={rows}
							defaultOrder={order}
							defaultOrderBy={orderBy}
						/>

						<ResourcePopup
							open={this.state.isResourcePopupOpen}
							onClose={this.handleCloseResourcePopup}
							resourceIds={this.state.resourceIdsPoppedUp}
							resourceMap={resourceMap}
						/>
					</Paper>

					{UPLOAD_BOL_ENABLED && (
						<CandoBolUploadDialog
							open={this.state.isUploadOpen}
							onClose={this.handleCloseUpload}
							uploadBol={candoBolUpload}
							showErrors={showErrors}
							onAfterSave={this.refreshTable}
						/>
					)}
				</Grid>
			</Grid>
		);
	}
}

CandoBols.propTypes = {
	classes: PropTypes.object.isRequired,
	candoBolSearch: PropTypes.func.isRequired,
	candoBolUpload: PropTypes.func.isRequired,
	candoBolReparse: PropTypes.func.isRequired,
	candoBols: PropTypes.array,
	isDwp: PropTypes.bool,

	showErrors: PropTypes.func.isRequired,
	resourceMap: PropTypes.object,
	stationMap: PropTypes.object,
	terminalMap: PropTypes.object,

	history: PropTypes.object,
	theme: PropTypes.object.isRequired,
	authUser: PropTypes.object,
};

export default _.flow([
	withStyles(styles, { withTheme: true }),
	AuthUserContainer,
	CandoBolsContainer,
])(CandoBols);
